import classNames from 'classnames';
import React, { PureComponent } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import colors from '@spotify-internal/encore-foundation';
import { formatPercentage } from '../../../../lib/Formatter';
import chartStyles from 'styles/charts.module.scss';
import { GenderWithPercentage } from '../../../../types';
import { injectIntl, FormattedMessage, IntlShape, defineMessage } from 'react-intl';

const GENDERS = ['female', 'male', 'notSpecified', 'nonBinary'];

const COLORS_BY_GENDER = {
  female: '#5a3f5a',
  male: '#bca5cf',
  notSpecified: '#ffd3d7',
  nonBinary: '#aacad8',
};

const FORMATTED_GENDER = {
  female: 'female',
  male: 'male',
  notSpecified: 'not specified',
  nonBinary: 'non-binary',
};

type Props = {
  genderData: GenderWithPercentage[];
  intl: IntlShape;
};

class InternationalizedGenderDemographicsChart extends PureComponent<Props> {
  valueAccessor = /* istanbul ignore next */ value =>
    formatPercentage(value.percentage, this.props.intl);

  tickFormatter = /* istanbul ignore next */ gender => {
    const message = defineMessage({
      id: `network.audience.gender.${FORMATTED_GENDER[gender]}`,
      description: `The label for the ${FORMATTED_GENDER[gender]} bar in the chart`,
      defaultMessage: FORMATTED_GENDER[gender],
    });

    return this.props.intl.formatMessage(message).toUpperCase();
  };

  renderBars = () =>
    this.props.genderData.map(({ gender }) => (
      <Cell key={`${gender}`} fill={COLORS_BY_GENDER[gender]} />
    ));

  renderLegend = () => (
    <Legend
      content={
        /* istanbul ignore next */ () => (
          <ul className={chartStyles.legend}>
            {GENDERS.map(gender => (
              <li key={gender} className={chartStyles['legend-item']}>
                <div
                  className={chartStyles.circle}
                  style={{ backgroundColor: COLORS_BY_GENDER[gender] }}
                />
                <span>
                  <FormattedMessage
                    id={`network.audience.gender.legend.${FORMATTED_GENDER[gender]}`}
                    description={`The label for the legend item '${FORMATTED_GENDER[gender]}'`}
                    defaultMessage={FORMATTED_GENDER[gender]}
                  />
                </span>
              </li>
            ))}
          </ul>
        )
      }
      wrapperStyle={{
        top: '-46px',
        textTransform: 'uppercase',
        color: colors['grey-40'],
      }}
    />
  );

  render() {
    return (
      <div data-qa="gender-demographics-chart" style={{ width: '100%', height: '350px' }}>
        <ResponsiveContainer height={350}>
          <BarChart
            data={this.props.genderData}
            margin={{ top: 25, right: 0, left: -20, bottom: 0 }}
            className={classNames(chartStyles.chart, chartStyles['bar-chart'])}
          >
            {this.renderLegend()}
            <XAxis
              axisLine={false}
              tickLine={false}
              dataKey="gender"
              tickFormatter={this.tickFormatter}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={value => formatPercentage(value, this.props.intl)}
              ticks={[0, 50, 100]}
            />
            <CartesianGrid vertical={false} />
            <Bar
              dataKey="percentage"
              fill="#bca5cf"
              minPointSize={2}
              stackId="percentStack"
              isAnimationActive={false}
              background={{ fill: 'rgba(241,241,241,.7)' }}
            >
              {this.renderBars()}
              <LabelList position="top" dataKey={this.valueAccessor} offset={10} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }
}

const GenderDemographicsChart = injectIntl(InternationalizedGenderDemographicsChart);
export { GenderDemographicsChart };
